// © 2026 KaraokeClubs. All rights reserved.
// Public "suggest an edit" modal shown on the venue detail page.
// A venue lead proposes corrections; the submission lands in the admin
// review queue (venue_suggestions) and is only applied once approved.

"use client";

import { useState } from "react";
import { t } from "@/lib/i18n";
import type { Venue, Locale } from "@/lib/types";

interface Props {
  venue: Venue;
  locale: Locale;
  open: boolean;
  onClose: () => void;
}

type SimpleField = "website" | "phone" | "instagram" | "facebook";

export default function SuggestEditModal({ venue, locale, open, onClose }: Props) {
  const [note, setNote] = useState("");
  const [submitter, setSubmitter] = useState("");
  const [fields, setFields] = useState<Record<SimpleField, string>>({
    website: venue.website ?? "",
    phone: venue.phone ?? "",
    instagram: venue.instagram ?? "",
    facebook: venue.facebook ?? "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  if (!open) return null;

  const editableFields: { key: SimpleField; label: string }[] = [
    { key: "website", label: t(locale, "suggest_field_website") },
    { key: "phone", label: t(locale, "suggest_field_phone") },
    { key: "instagram", label: t(locale, "suggest_field_instagram") },
    { key: "facebook", label: t(locale, "suggest_field_facebook") },
  ];

  async function submit() {
    // Build the changed simple fields (only those that actually differ).
    const changes: Record<string, unknown> = {};
    for (const { key } of editableFields) {
      const next = fields[key].trim();
      const current = (venue[key] ?? "") as string;
      if (next !== current) changes[key] = next || null;
    }

    const trimmedNote = note.trim();
    if (!trimmedNote && Object.keys(changes).length === 0) {
      setStatus("error");
      setErrorMsg(t(locale, "suggest_need_something"));
      return;
    }

    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/api/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_id: venue.id,
          changes,
          note: trimmedNote || null,
          submitter: submitter.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("done");
    } catch {
      setStatus("error");
      setErrorMsg(t(locale, "suggest_error"));
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-md max-h-[88vh] overflow-y-auto
                   bg-zinc-900 border border-white/[0.08] rounded-t-3xl sm:rounded-3xl
                   p-5 sm:p-6 shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* grab handle (mobile) */}
        <div className="flex justify-center mb-4 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        {status === "done" ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-violet-500/15 flex items-center justify-center">
              <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-[15px] text-zinc-200 mb-6">{t(locale, "suggest_thanks")}</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-violet-600 text-white font-semibold text-sm active:scale-95 transition-all"
            >
              {t(locale, "cancel")}
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-bold mb-1">{t(locale, "suggest_title")}</h2>
            <p className="text-[13px] text-zinc-400 mb-5 leading-relaxed">{t(locale, "suggest_intro")}</p>

            {/* Free-text note — the primary way to describe hours / anything */}
            <label className="block text-[13px] font-semibold text-zinc-300 mb-1.5">
              {t(locale, "suggest_note")}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={t(locale, "suggest_note_ph")}
              className="w-full mb-4 px-3 py-2.5 rounded-xl bg-zinc-800/80 border border-white/[0.08]
                         text-[14px] text-zinc-100 placeholder:text-zinc-600 resize-none
                         focus:outline-none focus:border-violet-500/50"
            />

            {/* Optional simple field edits */}
            <div className="space-y-3 mb-4">
              {editableFields.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-[12px] font-medium text-zinc-400 mb-1">{label}</label>
                  <input
                    type="text"
                    value={fields[key]}
                    onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                               text-[13px] text-zinc-100 placeholder:text-zinc-600
                               focus:outline-none focus:border-violet-500/50"
                  />
                </div>
              ))}
            </div>

            {/* Optional contact */}
            <label className="block text-[12px] font-medium text-zinc-400 mb-1">
              {t(locale, "suggest_contact")}
            </label>
            <input
              type="text"
              value={submitter}
              onChange={(e) => setSubmitter(e.target.value)}
              placeholder={t(locale, "suggest_contact_ph")}
              className="w-full mb-4 px-3 py-2 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                         text-[13px] text-zinc-100 placeholder:text-zinc-600
                         focus:outline-none focus:border-violet-500/50"
            />

            {status === "error" && (
              <p className="text-[13px] text-red-400 mb-3">{errorMsg}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-zinc-800 border border-white/[0.06] text-zinc-300 font-semibold text-sm active:scale-[0.98] transition-all"
              >
                {t(locale, "cancel")}
              </button>
              <button
                onClick={submit}
                disabled={status === "sending"}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 text-white font-bold text-sm
                           active:scale-[0.98] transition-all disabled:opacity-60 shadow-lg shadow-violet-900/30"
              >
                {status === "sending" ? t(locale, "suggest_sending") : t(locale, "suggest_submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
